import { ShortcutCommand, ShortcutContext } from '../hooks/useShortcutManager';
import i18n from '../lib/i18n';

// 优先级常量
export const PRIORITY = {
  CRITICAL: 200,    // 关键系统快捷键（如ESC关闭模态框）
  GLOBAL: 150,      // 全局快捷键（如Cmd+K, Cmd+I）
  PAGE: 100,        // 页面级快捷键
  INPUT: 50,        // 输入框内快捷键
  LOW: 10           // 低优先级快捷键
} as const;

// 全局快捷键动作函数类型
export interface GlobalShortcutActions {
  smartChatSwitch: () => void;
  smartTaskSwitch: () => void;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
  closeModal: () => void;
}

// TaskV2 快捷键动作函数类型
export interface TaskV2ShortcutActions {
  goToPrevDay: () => void;
  goToNextDay: () => void;
  goToToday: () => void;
  focusPrevTask: () => void;
  focusNextTask: () => void;
  toggleTask: () => void;
  editTask: () => void;
  newTask: () => void;
  toggleViewMode: () => void;
  resetFocus: () => void;
  undo: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  closeTaskDetail: () => void;
  isTaskDetailOpen: () => boolean;
}

// Chat 快捷键动作函数类型
export interface ChatShortcutActions {
  toggleVoiceRecording: () => void;
  cancelRecording: () => void;
  isVoiceRecording: () => boolean;
}

// 创建全局快捷键定义的工厂函数
export const createGlobalShortcuts = (actions: GlobalShortcutActions): ShortcutCommand[] => [
  {
    id: 'global.smart-chat-switch',
    keys: ['cmd+k', 'ctrl+k'],
    priority: PRIORITY.GLOBAL,
    enableOnFormTags: true, // 在输入框中也要生效
    condition: () => true,
    action: () => {
      actions.smartChatSwitch();
    },
    description: i18n.t('common:keymap.smartChatSwitch', '智能切换到聊天'),
    category: i18n.t('common:keymap.global', '全局')
  },
  {
    id: 'global.smart-task-switch',
    keys: ['cmd+i', 'ctrl+i'],
    priority: PRIORITY.GLOBAL,
    enableOnFormTags: true, // 在输入框中也要生效
    condition: () => true,
    action: () => {
      actions.smartTaskSwitch();
    },
    description: i18n.t('common:keymap.smartTaskSwitch', '智能切换到任务'),
    category: i18n.t('common:keymap.global', '全局')
  },
  {
    id: 'global.toggle-left-sidebar',
    keys: ['cmd+shift+<', 'cmd+shift+,', 'ctrl+shift+<', 'ctrl+shift+,'],
    priority: PRIORITY.GLOBAL,
    condition: (context: ShortcutContext) => context.layoutMode === 'desktop',
    action: () => {
      actions.toggleLeftSidebar();
    },
    description: i18n.t('common:keymap.toggleLeftSidebar', '折叠/展开左栏'),
    category: i18n.t('common:keymap.desktop', '桌面端')
  },
  {
    id: 'global.toggle-right-sidebar',
    keys: ['cmd+shift+>', 'cmd+shift+.', 'ctrl+shift+>', 'ctrl+shift+.'],
    priority: PRIORITY.GLOBAL,
    condition: (context: ShortcutContext) => context.layoutMode === 'desktop',
    action: () => {
      actions.toggleRightSidebar();
    },
    description: i18n.t('common:keymap.toggleRightSidebar', '折叠/展开右栏'),
    category: i18n.t('common:keymap.desktop', '桌面端')
  },
  {
    id: 'global.close-modal',
    keys: ['esc'],
    priority: PRIORITY.CRITICAL,
    condition: (context: ShortcutContext) => context.isModalOpen,
    action: () => {
      actions.closeModal();
    },
    description: i18n.t('common:keymap.closeModal', '关闭弹窗'),
    category: i18n.t('common:keymap.global', '全局')
  }
];

// 默认的全局快捷键（仅用于测试和调试，不用于实际注册）
// 实际的快捷键注册通过 useShortcutRegistration 中的工厂函数完成
export const GLOBAL_SHORTCUTS: ShortcutCommand[] = createGlobalShortcuts({
  smartChatSwitch: () => console.log('Smart chat switch - no action bound'),
  smartTaskSwitch: () => console.log('Smart task switch - no action bound'),
  toggleLeftSidebar: () => console.log('Toggle left sidebar - no action bound'),
  toggleRightSidebar: () => console.log('Toggle right sidebar - no action bound'),
  closeModal: () => console.log('Close modal - no action bound')
});

// 创建 TaskV2 快捷键定义的工厂函数
export const createTaskV2Shortcuts = (actions: TaskV2ShortcutActions): ShortcutCommand[] => [
  {
    id: 'taskv2.prev-day',
    keys: ['left', 'arrowleft'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.goToPrevDay();
    },
    description: i18n.t('common:keymap.switchToPrevDay', '切换到前一天'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.next-day',
    keys: ['right', 'arrowright'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.goToNextDay();
    },
    description: i18n.t('common:keymap.switchToNextDay', '切换到后一天'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.focus-prev-task',
    keys: ['up', 'arrowup'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.focusPrevTask();
    },
    description: i18n.t('common:keymap.selectPrevTask', '选中上一个任务/向上滚动'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.focus-next-task',
    keys: ['down', 'arrowdown'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.focusNextTask();
    },
    description: i18n.t('common:keymap.selectNextTask', '选中下一个任务/向下滚动'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.toggle-task',
    keys: ['space'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.toggleTask();
    },
    description: i18n.t('common:keymap.toggleTaskComplete', '切换任务完成状态'),
    category: i18n.t('common:keymap.taskOperations', '任务操作')
  },
  {
    id: 'taskv2.edit-task',
    keys: ['enter'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.editTask();
    },
    description: i18n.t('common:keymap.editTask', '编辑任务'),
    category: i18n.t('common:keymap.taskOperations', '任务操作')
  },
  {
    id: 'taskv2.new-task',
    keys: ['cmd+enter', 'ctrl+enter'],
    priority: PRIORITY.PAGE,
    enableOnFormTags: true, // 在输入框中也要生效
    condition: (context: ShortcutContext) => context.currentPage === 'taskv2',
    action: () => {
      actions.newTask();
    },
    description: i18n.t('common:keymap.createTask', '创建任务'),
    category: i18n.t('common:keymap.taskOperations', '任务操作')
  },
  {
    id: 'taskv2.toggle-view-mode',
    keys: ['t'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.toggleViewMode();
    },
    description: i18n.t('common:keymap.toggleTimelineMode', '切换时间轴模式'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.go-to-today',
    keys: ['h'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.goToToday();
    },
    description: i18n.t('common:keymap.goToToday', '回到今天'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.close-task-detail',
    keys: ['esc'],
    priority: PRIORITY.PAGE + 10, // 比普通页面快捷键优先级稍高
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && actions.isTaskDetailOpen() && !context.isModalOpen,
    action: () => {
      actions.closeTaskDetail();
    },
    description: i18n.t('common:keymap.closeModal', '关闭任务详情'),
    category: i18n.t('common:keymap.taskOperations', '任务操作')
  },
  {
    id: 'taskv2.reset-focus',
    keys: ['esc'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !context.isModalOpen && !actions.isTaskDetailOpen(),
    action: () => {
      actions.resetFocus();
    },
    description: i18n.t('common:keymap.cancelSelection', '取消选中'),
    category: i18n.t('common:keymap.taskOperations', '任务操作')
  },
  {
    id: 'taskv2.undo',
    keys: ['cmd+z', 'ctrl+z'],
    priority: PRIORITY.PAGE,
    enableOnFormTags: true, // 在输入框中也要生效
    condition: (context: ShortcutContext) => context.currentPage === 'taskv2',
    action: () => {
      actions.undo();
    },
    description: i18n.t('common:keymap.undoOperation', '撤销操作'),
    category: i18n.t('common:keymap.taskOperations', '任务操作')
  },
  {
    id: 'taskv2.zoom-in',
    keys: ['+', '='],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.zoomIn();
    },
    description: i18n.t('common:keymap.zoomInTimeline', '放大时间轴'),
    category: i18n.t('common:keymap.navigation', '导航')
  },
  {
    id: 'taskv2.zoom-out',
    keys: ['-'],
    priority: PRIORITY.PAGE,
    condition: (context: ShortcutContext) =>
      context.currentPage === 'taskv2' && !context.isInputFocused && !actions.isTaskDetailOpen(),
    action: () => {
      actions.zoomOut();
    },
    description: i18n.t('common:keymap.zoomOutTimeline', '缩小时间轴'),
    category: i18n.t('common:keymap.navigation', '导航')
  }
];

// 默认的 TaskV2 快捷键（仅用于测试和调试，不用于实际注册）
// 实际的快捷键注册通过 useShortcutRegistration 中的工厂函数完成
export const TASKV2_SHORTCUTS: ShortcutCommand[] = createTaskV2Shortcuts({
  goToPrevDay: () => console.log('TaskV2: Go to previous day - no action bound'),
  goToNextDay: () => console.log('TaskV2: Go to next day - no action bound'),
  goToToday: () => console.log('TaskV2: Go to today - no action bound'),
  focusPrevTask: () => console.log('TaskV2: Focus previous task - no action bound'),
  focusNextTask: () => console.log('TaskV2: Focus next task - no action bound'),
  toggleTask: () => console.log('TaskV2: Toggle task - no action bound'),
  editTask: () => console.log('TaskV2: Edit task - no action bound'),
  newTask: () => console.log('TaskV2: New task - no action bound'),
  toggleViewMode: () => console.log('TaskV2: Toggle view mode - no action bound'),
  resetFocus: () => console.log('TaskV2: Reset focus - no action bound'),
  undo: () => console.log('TaskV2: Undo - no action bound'),
  zoomIn: () => console.log('TaskV2: Zoom in - no action bound'),
  zoomOut: () => console.log('TaskV2: Zoom out - no action bound'),
  closeTaskDetail: () => console.log('TaskV2: Close task detail - no action bound'),
  isTaskDetailOpen: () => false
});

// 创建 Chat 快捷键定义的工厂函数
export const createChatShortcuts = (actions: ChatShortcutActions): ShortcutCommand[] => [
  {
    id: 'chat.toggle-voice-recording',
    keys: ['cmd+u', 'ctrl+u'],
    priority: PRIORITY.PAGE,
    enableOnFormTags: true, // 在输入框中也要生效
    condition: (context: ShortcutContext) =>
      context.currentPage === 'chat' || context.layoutMode === 'desktop',
    action: () => {
      actions.toggleVoiceRecording();
    },
    description: i18n.t('common:keymap.startStopRecording', '开始/结束录音'),
    category: i18n.t('common:keymap.chatOperations', '聊天操作')
  },
  {
    id: 'chat.cancel-recording',
    keys: ['esc'],
    priority: PRIORITY.PAGE + 10, // 比普通页面快捷键优先级稍高
    condition: (context: ShortcutContext) =>
      (context.currentPage === 'chat' || context.layoutMode === 'desktop') &&
      actions.isVoiceRecording() && !context.isModalOpen,
    action: () => {
      actions.cancelRecording();
    },
    description: i18n.t('common:keymap.cancelRecording', '取消录音'),
    category: i18n.t('common:keymap.chatOperations', '聊天操作')
  }
  // 注意：粘贴文件上传功能由独立的全局粘贴监听器处理，不在快捷键系统中定义
  // 注意：移除了 chat.send-message 快捷键定义，因为 TextInput 组件已经处理了回车发送功能
  // 这样避免了重复定义和事件冲突
];

// 默认的 Chat 快捷键（仅用于测试和调试，不用于实际注册）
// 实际的快捷键注册通过 useShortcutRegistration 中的工厂函数完成
export const CHAT_SHORTCUTS: ShortcutCommand[] = createChatShortcuts({
  toggleVoiceRecording: () => console.log('Chat: Toggle voice recording - no action bound'),
  cancelRecording: () => console.log('Chat: Cancel recording - no action bound'),
  isVoiceRecording: () => false
});

// 帮助快捷键 - 已禁用长按 CMD 功能
export const HELP_SHORTCUTS: ShortcutCommand[] = [
  // 禁用长按显示快捷键功能
  // {
  //   id: 'help.show-keymap',
  //   keys: ['cmd', 'ctrl'], // 这个由 useShortcutManager 中的长按逻辑处理
  //   priority: PRIORITY.GLOBAL,
  //   condition: () => false, // 由长按逻辑处理，不在常规快捷键中生效
  //   action: (context: ShortcutContext) => {
  //     console.log('Show keymap triggered', context);
  //   },
  //   description: i18n.t('common:keymap.showKeymap', '长按显示快捷键说明'),
  //   category: i18n.t('common:keymap.help', '帮助')
  // }
];

// 所有快捷键的集合（仅用于测试完整性和调试）
// 实际的快捷键注册通过 useShortcutRegistration 动态管理
export const ALL_SHORTCUTS = [
  ...GLOBAL_SHORTCUTS,
  ...TASKV2_SHORTCUTS,
  ...CHAT_SHORTCUTS,
  ...HELP_SHORTCUTS
];
